import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Drawer,
  DrawerBody,
  Dropdown,
  mergeClasses,
  Option,
  Spinner,
  Text,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  Mic20Regular,
  Mic28Regular,
  Pause28Regular,
  Send16Regular,
  SkipForwardTabFilled,
  TextBulletListLtr20Regular,
} from "@fluentui/react-icons";
import { addCompletedInterview } from "api";
import { ConstantValues, PAGE_TITLES } from "common/constants";
import { useIsMobile } from "hooks/useIsMobile";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { showToast } from "../../../redux/toastActions";
import {
  deleteExternalSession,
  getAITopic,
  getNextObjectiveExternal,
  getSessionInfo,
  getSpeechToken,
  skipObjectiveExternal,
  submitExternalAnswer,
} from "../api";
import {
  ANSWER_IS_REQUIRED,
  DEFAULT_VOICE_LOCALE,
  MAX_ANSWER_LENGTH,
  MAXIMUM_ANSWER_LENGTH_ALLOWED,
  MINIMUM_ANSWER_LENGTH,
  MINIMUM_ANSWER_LENGTH_REQUIRED,
  VOICE_OPTIONS,
  VoiceOption,
} from "../common/constants";
import { useAutosizeTextarea } from "../components/useAutosizeTextArea";
import { MicrophoneHelpDialog } from "../components/voice/MicrophoneHelpDialog";
import { VoiceControls } from "../components/voice/voiceControls/VoiceControls";
import { VoiceControlsHandle } from "../components/voice/voiceControls/voiceControls.types";
import { IAnswer, ICreateAnswer } from "../models/IAnswer";
import { INextObjectiveResponse } from "../models/INextQuestionResponse";
import {
  DEFAULT_LIKERT_SCALE_LABELS,
  getOptionLabel,
  IObjective,
  OBJECTIVE_TYPE_LIKERT_SCALE,
  OBJECTIVE_TYPE_MULTIPLE_CHOICE,
} from "../models/IObjective";
import { AnswersHistory } from "./AnswersHistory/answersHistory";
import { buildAccentPalette } from "utils/accentPalette";
import { CurrentQuestionShelf } from "./CurrentQuestionShelf/currentQuestionShelf";
import { LikertScalePicker } from "./LikertScalePicker/LikertScalePicker";
import { MultipleChoicePicker } from "./MultipleChoicePicker/MultipleChoicePicker";
import {
  IExternalDiagnosticsProps,
  Tab,
  VoiceUiState,
} from "./externalDiagnostic.types";
import { useLayoutStyles, useStyles } from "./externalDiagnostics.styles";
import { InterviewSidebar } from "./InterviewSidebar/InterviewSidebar";
import { PoweredByFooter } from "./PoweredByFooter/PoweredByFooter";

const SUBMIT_HOVER = "Submit your response";
const INITIAL_LOADING_DELAY_MS = 1000;
const MISSING_ANSWER_HOVER = "Enter a response above to continue.";
const SKIP_CONFIRM_TITLE = "Skip this objective?";
const SKIP_CONFIRM_MESSAGE =
  "You won't be asked about this objective again. Are you sure you want to skip it?";

const TABS = {
  TEXT: "text" as Tab,
  VOICE: "voice" as Tab,
} as const;

const VOICE_STATES = {
  ASKING: "asking" as VoiceUiState,
  LISTENING: "listening" as VoiceUiState,
  PAUSED: "paused" as VoiceUiState,
} as const;

const TAB_VOICE_STATE: Record<Tab, VoiceUiState> = {
  text: VOICE_STATES.ASKING,
  voice: VOICE_STATES.ASKING,
};

export const ExternalDiagnostics = (
  props: IExternalDiagnosticsProps,
): JSX.Element => {
  const hasFetchedInitialData = useRef<boolean>(false);
  const styles = useStyles();
  const layoutStyles = useLayoutStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const dispatch = useDispatch();

  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState<boolean>(false);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const [currentObjective, setCurrentObjective] = useState<
    INextObjectiveResponse["objective"] | null
  >(props.initialNextObjective?.objective ?? null);
  const [previousAnswers, setPreviousAnswers] = useState<IAnswer[]>(
    props.initialNextObjective?.previousAnswers ?? [],
  );
  const [answer, setAnswer] = useState<string>(ConstantValues.EMPTY_STRING);
  const [pendingAnswer, setPendingAnswer] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [finished, setFinished] = useState<boolean>(
    Boolean(props.initialNextObjective?.finished),
  );
  const [error, setError] = useState<string | null>(null);
  const [lastQuestionTopic, setLastQuestionTopic] = useState<string | null>(
    null,
  );
  const [endedOnDemographics, setEndedOnDemographics] =
    useState<boolean>(false);
  const [distributionSpeechEnabled, setDistributionSpeechEnabled] =
    useState<boolean>(true);
  const [selectedLikertValue, setSelectedLikertValue] = useState<string>("");
  const [selectedMultipleChoiceValues, setSelectedMultipleChoiceValues] =
    useState<string[]>([]);
  const historyListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (historyListRef.current) {
      historyListRef.current.scrollTop = historyListRef.current.scrollHeight;
    }
  }, [previousAnswers, pendingAnswer, loading, initialLoading]);

  useEffect(() => {
    setSelectedLikertValue("");
    setSelectedMultipleChoiceValues([]);
  }, [currentObjective]);

  useEffect(() => {
    const fixTabsterSentinels = () => {
      document
        .querySelectorAll<HTMLElement>(
          '[data-tabster-dummy][aria-hidden="true"]',
        )
        .forEach((el) => {
          el.setAttribute("tabindex", "-1");
        });
    };

    fixTabsterSentinels();

    const observer = new MutationObserver(fixTabsterSentinels);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  const [inputTab, setInputTab] = useState<Tab>(TABS.TEXT);
  const [voiceUiState, setVoiceUiState] = useState<VoiceUiState>(
    TAB_VOICE_STATE[TABS.TEXT],
  );
  const [hoveredVoiceBtn, setHoveredVoiceBtn] = useState<string | null>(null);
  const voiceEnabled = inputTab === TABS.VOICE;
  const [selectedLanguage, setSelectedLanguage] = useState<VoiceOption>(
    VOICE_OPTIONS.find((opt) => opt.key === "en-US") || VOICE_OPTIONS[1],
  );
  const [switchingLanguages, setSwitchingLanguages] = useState<boolean>(false);
  const voiceLocale = selectedLanguage.locale || DEFAULT_VOICE_LOCALE;
  const [micDeviceId, setMicDeviceId] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  const voiceControlsRef = useRef<VoiceControlsHandle>(null);

  const handleListeningStart = useCallback(
    () => setVoiceUiState("listening"),
    [],
  );

  const handleAskingStart = useCallback(() => setVoiceUiState("asking"), []);

  const [voiceIsDetecting, setVoiceIsDetecting] = useState(false);
  const [voiceSilenceTimer, setVoiceSilenceTimer] = useState(3);
  const handleVoiceStateUpdate = useCallback(
    (isDetecting: boolean, silenceTimer: number) => {
      setVoiceIsDetecting(isDetecting);
      setVoiceSilenceTimer(silenceTimer);
    },
    [],
  );

  const { textareaRef, onInput } = useAutosizeTextarea(answer, {
    maxHeight: "40vh",
  });

  const accentPalette = useMemo(
    () =>
      props.diagnostic?.customBrandingEnabled
        ? buildAccentPalette(
            props.diagnostic.accentColor,
            props.diagnostic.headerColor,
          )
        : undefined,
    [
      props.diagnostic?.customBrandingEnabled,
      props.diagnostic?.accentColor,
      props.diagnostic?.headerColor,
    ],
  );

  const sidebarObjectives = useMemo<IObjective[]>(() => {
    const realObjectives = props.diagnostic?.objectives ?? [];
    const demographicsCount =
      props.diagnostic?.demographicsQuestions?.length ?? 0;
    if (demographicsCount === 0) return realObjectives;

    const placement = props.diagnostic?.demographicsPlacement ?? "beginning";
    const realOrders = realObjectives
      .map((o) => o.order)
      .filter((o): o is number => typeof o === "number");
    const demographicsOrder =
      placement === "end"
        ? (realOrders.length ? Math.max(...realOrders) : 0) + 1
        : -1;

    const demographicsObjective: IObjective = {
      _id: "__demographics__",
      name: "Demographics",
      text: "Demographics",
      questionNumber: 0,
      order: demographicsOrder,
      user: { userId: "", displayName: "" },
    };

    return placement === "end"
      ? [...realObjectives, demographicsObjective]
      : [demographicsObjective, ...realObjectives];
  }, [
    props.diagnostic?.objectives,
    props.diagnostic?.demographicsQuestions,
    props.diagnostic?.demographicsPlacement,
  ]);

  const getVoiceBtnStyle = (btnId: string): React.CSSProperties | undefined => {
    if (!accentPalette) return undefined;
    return hoveredVoiceBtn === btnId
      ? {
          color: accentPalette.accentTextDark,
          borderColor: accentPalette.accent,
          backgroundColor: accentPalette.tint25,
        }
      : {
          color: accentPalette.accentTextDark,
          borderColor: accentPalette.accent,
        };
  };

  const voicePrimaryButtonStyle = accentPalette
    ? {
        backgroundColor: accentPalette.accent,
        color: accentPalette.accentText,
      }
    : undefined;
  const [countdownSeconds, setCountdownSeconds] = useState<number>(0);
  const onInterviewCompleteRef = useRef(props.onInterviewComplete);

  useEffect(() => {
    onInterviewCompleteRef.current = props.onInterviewComplete;
  }, [props.onInterviewComplete]);

  useEffect(() => {
    if (props.diagnostic?.name) {
      document.title = props.diagnostic.name;
    }
    return () => {
      document.title = PAGE_TITLES.NH_DIAGNOSTICS;
    };
  }, [props.diagnostic?.name]);

  useEffect(() => {
    if (!finished) return;

    setIsSidebarOpen(false);

    if (countdownSeconds === 0) {
      setCountdownSeconds(10);
    }
  }, [finished]);

  useEffect(() => {
    if (countdownSeconds <= 0) return;

    const timer = setTimeout(() => {
      const next = countdownSeconds - 1;
      setCountdownSeconds(next);
      if (next === 0) {
        onInterviewCompleteRef.current?.();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdownSeconds]);

  useEffect(() => {
    if (
      !finished ||
      (!lastQuestionTopic && !endedOnDemographics) ||
      !voiceEnabled
    )
      return;

    const speakFinalQuestion = async () => {
      try {
        const { token, region } = await getSpeechToken();
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
          token,
          region,
        );
        speechConfig.speechRecognitionLanguage = voiceLocale;
        speechConfig.speechSynthesisVoiceName =
          selectedLanguage.voiceName || "en-US-JennyMultilingualNeural";
        const audioOut = sdk.AudioConfig.fromDefaultSpeakerOutput();
        const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioOut);

        synthesizer.speakTextAsync(
          buildFinalMessage(),
          () => synthesizer.close(),
          (err) => {
            console.error("Speech synthesis error:", err);
            synthesizer.close();
          },
        );
      } catch (err) {
        console.error("Speech setup error:", err);
      }
    };

    speakFinalQuestion();
  }, [
    finished,
    lastQuestionTopic,
    endedOnDemographics,
    voiceEnabled,
    voiceLocale,
    selectedLanguage,
  ]);

  useEffect(() => {
    onInput();
  }, [currentObjective?.text, voiceEnabled, onInput]);

  useEffect(() => {
    if (!isMobile) {
      setIsSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (voiceEnabled && currentObjective) {
      setVoiceUiState("asking");
    }
  }, [currentObjective?.text, voiceEnabled]);

  useEffect(() => {
    if (loading && voiceEnabled) {
      voiceControlsRef.current?.pause();
      setVoiceUiState("loading");
    }
  }, [loading, voiceEnabled]);

  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    (async () => {
      try {
        const sessionInfo = await getSessionInfo();
        if (sessionInfo?.speechEnabled !== undefined) {
          setDistributionSpeechEnabled(sessionInfo.speechEnabled);
        }
      } catch (error) {
        console.error("Failed to fetch session info", error);
      }

      if (!props.initialNextObjective) {
        await fetchNext(ConstantValues.EMPTY_STRING, selectedLanguage.text);
      } else {
        await new Promise((r) => setTimeout(r, INITIAL_LOADING_DELAY_MS));
        setInitialLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchLastQuestionTopic = async () => {
      const generatedTopic = await getAITopic({
        messages: [currentObjective!.text],
      });
      if (!generatedTopic?.topic) {
        return;
      }

      const sanitizedTopic: string = generatedTopic.topic
        .replace(/^"|"$/g, ConstantValues.EMPTY_STRING)
        .trim();
      if (sanitizedTopic === ConstantValues.EMPTY_STRING) {
        return;
      }

      setLastQuestionTopic(sanitizedTopic);
    };

    if (currentObjective && !currentObjective.demographicQuestionId) {
      (async () => {
        await fetchLastQuestionTopic();
      })();
    }
  }, [currentObjective]);

  const fetchNext = async (
    distributionIdToUse: string,
    language: string,
  ): Promise<void> => {
    try {
      const result = await getNextObjectiveExternal({
        finished: false,
        distributionId: distributionIdToUse,
        language: language,
      });

      setCurrentObjective(result.objective ?? null);
      setPreviousAnswers(result.previousAnswers ?? []);
      setFinished(result.finished);

      if (result.finished) deleteExternalSession();
    } catch (error) {
      console.error("Failed to fetch next objective", error);
      dispatch(showToast("Failed to fetch next objective", "error"));
    } finally {
      setInitialLoading(false);
    }
  };

  const advanceToNextObjective = async (
    isDemographic: boolean,
  ): Promise<void> => {
    const result = await getNextObjectiveExternal({
      finished: false,
      language: selectedLanguage.text,
    });

    if (result.finished) {
      deleteExternalSession();
      setFinished(true);
      setEndedOnDemographics(isDemographic);
      await addCompletedInterview();
      setCurrentObjective(null);
      setPreviousAnswers(result.previousAnswers ?? []);
      return;
    }

    setPendingAnswer(ConstantValues.EMPTY_STRING);
    setCurrentObjective(result.objective ?? null);
    setPreviousAnswers(result.previousAnswers ?? []);
  };

  const getSortedSelectedPositions = (): string =>
    [...selectedMultipleChoiceValues]
      .sort((first, second) => Number(first) - Number(second))
      .join(",");

  const getSelectedOptionsText = (selectedPositions: string): string => {
    const separator = selectedLanguage.locale === "ar-SA" ? "، " : ", ";
    return selectedPositions
      .split(",")
      .map((position) => currentObjective?.selectOptions?.[position])
      .filter(Boolean)
      .join(separator);
  };

  const doSubmit = async (textToSubmit: string): Promise<void> => {
    const realAnswer = textToSubmit || answer;
    if (!realAnswer.trim() || !currentObjective) return;

    const validationError = validateTextAreaValue(realAnswer);
    if (validationError) {
      setError(validationError);
      return;
    }

    setAnswer(ConstantValues.EMPTY_STRING);
    setPendingAnswer(
      isLikertObjective
        ? getOptionLabel(realAnswer, currentObjective.selectOptions)
        : isMultipleChoiceObjective
          ? getSelectedOptionsText(realAnswer)
          : realAnswer,
    );
    setLoading(true);

    const isDemographic = Boolean(currentObjective.demographicQuestionId);

    const answerPayload: ICreateAnswer = isDemographic
      ? {
          demographicQuestionId: currentObjective.demographicQuestionId,
          answer: realAnswer,
          language: selectedLanguage.text,
        }
      : {
          questionNumber: currentObjective.questionNumber,
          objectiveText: currentObjective.text,
          objectiveTextInEnglish: currentObjective.textInEnglish,
          answer: realAnswer,
          finalQuestion: false,
          isInternal: false,
          language: selectedLanguage.text,
          answerType: voiceEnabled ? "voice" : "text",
        };

    try {
      await submitExternalAnswer({ answerPayload, isFinal: false });
      await advanceToNextObjective(isDemographic);
    } catch (error) {
      console.error("Failed to submit answer", error);
      dispatch(showToast("Failed to submit answer", "error"));
    } finally {
      setLoading(false);
      setPendingAnswer(ConstantValues.EMPTY_STRING);
    }
  };

  const doSkip = async (): Promise<void> => {
    if (!currentObjective || loading) return;

    setLoading(true);

    const objectivePayload = {
      questionNumber: currentObjective.questionNumber,
      objectiveText: currentObjective.text,
      objectiveTextInEnglish: currentObjective.textInEnglish,
      language: selectedLanguage.text,
    };

    try {
      await skipObjectiveExternal({ objectivePayload });
      await advanceToNextObjective(false);
    } catch (error) {
      console.error("Failed to skip objective", error);
      dispatch(showToast("Failed to skip objective", "error"));
    } finally {
      setLoading(false);
      setPendingAnswer(ConstantValues.EMPTY_STRING);
    }
  };

  const requestSkip = (): void => {
    if (!currentObjective || loading) return;
    setShowSkipConfirm(true);
  };

  const confirmSkip = (): void => {
    setShowSkipConfirm(false);
    void doSkip();
  };

  const canSkip =
    !currentObjective?.demographicQuestionId &&
    currentObjective?.allowSkipping !== false;

  const skipButton = canSkip ? (
    <Tooltip content="Skip to next objective" relationship="label">
      <button
        className={mergeClasses(
          styles.skipBtn,
          loading && styles.skipBtnDisabled,
        )}
        disabled={loading}
        onClick={requestSkip}
      >
        <SkipForwardTabFilled />
      </button>
    </Tooltip>
  ) : null;

  const skipButtonVoice = canSkip ? (
    <Button
      className={mergeClasses(
        sharedButtonStyles.outlineButton,
        styles.skipBtnVoice,
      )}
      size="small"
      disabled={loading}
      onClick={requestSkip}
    >
      Skip objective
    </Button>
  ) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doSubmit(answer);
  };

  const buildFinalMessage = () => {
    const separator = selectedLanguage.locale === "ar-SA" ? "،" : ",";
    if (endedOnDemographics) {
      return `${selectedLanguage.demographicsEnding}${separator} ${selectedLanguage.finalEndings[1]} ${selectedLanguage.finalEndings[2]}`;
    }
    return lastQuestionTopic
      ? `${selectedLanguage.finalEndings[0]} ${lastQuestionTopic}${separator} ${selectedLanguage.finalEndings[1]} ${selectedLanguage.finalEndings[2]}`
      : `${selectedLanguage.finalEndings[3]} ${selectedLanguage.finalEndings[2]}`;
  };

  const validateTextAreaValue = (value: string): string | null => {
    const trimmedValue: string = value.trim();
    const isDemographic = Boolean(currentObjective?.demographicQuestionId);

    if (voiceEnabled) {
      if (trimmedValue.length === 0) return "No answer was detected.";
      if (!isDemographic && trimmedValue.length < MINIMUM_ANSWER_LENGTH)
        return "Please provide a little more details.";
      return null;
    }

    if (trimmedValue.length === 0) return ANSWER_IS_REQUIRED;

    if (!isDemographic && trimmedValue.length < MINIMUM_ANSWER_LENGTH)
      return MINIMUM_ANSWER_LENGTH_REQUIRED;

    if (trimmedValue.length > MAX_ANSWER_LENGTH)
      return MAXIMUM_ANSWER_LENGTH_ALLOWED;

    return null;
  };

  // readOnly (not disabled) so the textarea keeps focus while an answer is submitting
  const isAnswerInputBusy = loading || switchingLanguages;

  const hasAnswer = answer.trim().split(/\s+/).filter(Boolean).length > 0;
  const isLikertObjective =
    currentObjective?.objectiveType === OBJECTIVE_TYPE_LIKERT_SCALE;
  const isMultipleChoiceObjective =
    currentObjective?.objectiveType === OBJECTIVE_TYPE_MULTIPLE_CHOICE;
  const isSelectionObjective = isLikertObjective || isMultipleChoiceObjective;

  if (initialLoading) {
    return (
      <div
        className={styles.spinnerWrapper}
        style={
          accentPalette
            ? ({
                "--colorBrandStroke1": accentPalette.accent,
                "--colorBrandStroke2Contrast": accentPalette.headerBg,
              } as React.CSSProperties)
            : undefined
        }
      >
        <Spinner size="huge" aria-label="Loading NH Diagnostic IQ" />
        <Text size={600} weight="bold">
          Loading NH Diagnostic IQ...
        </Text>
      </div>
    );
  }

  const canSubmit = isLikertObjective
    ? Boolean(selectedLikertValue)
    : isMultipleChoiceObjective
      ? selectedMultipleChoiceValues.length > 0
      : !error && hasAnswer;
  const submitButton = (
    <Tooltip
      content={canSubmit ? SUBMIT_HOVER : MISSING_ANSWER_HOVER}
      relationship="description"
    >
      <button
        className={mergeClasses(
          styles.submitBtn,
          (loading || !canSubmit) && styles.submitBtnDisabled,
        )}
        style={
          accentPalette
            ? {
                backgroundColor: accentPalette.accent,
                color: accentPalette.accentText,
              }
            : undefined
        }
        disabled={loading || !canSubmit}
        onClick={
          isLikertObjective
            ? () => void doSubmit(selectedLikertValue)
            : isMultipleChoiceObjective
              ? () => void doSubmit(getSortedSelectedPositions())
              : handleSubmit
        }
        aria-label="Submit answer"
      >
        {loading ? (
          <Spinner size="tiny" aria-label="Sending answer" />
        ) : (
          <Send16Regular style={{ transform: "rotate(-90deg)" }} />
        )}
      </button>
    </Tooltip>
  );

  if (finished) {
    const firstAnswer = { ...previousAnswers[0] };
    firstAnswer.objectiveText = buildFinalMessage();
    firstAnswer.answer = ConstantValues.EMPTY_STRING;
    firstAnswer.endingMessageHTML =
      props.diagnostic?.endingMessage ||
      "Thank you for completing this interview!";

    return (
      <div className={layoutStyles.page}>
        <div className={layoutStyles.interviewLayout}>
          {isMobile ? (
            <Drawer
              type="overlay"
              position="start"
              open={isSidebarOpen}
              onOpenChange={(_, { open }) => {
                setIsSidebarOpen(open);
                if (!open) {
                  toggleButtonRef.current?.focus();
                }
              }}
              className={layoutStyles.sidebarDrawer}
              aria-label="Interview objectives"
            >
              <DrawerBody className={layoutStyles.sidebarDrawerBody}>
                <InterviewSidebar
                  diagnosticName={props.diagnostic?.name ?? ""}
                  objectives={sidebarObjectives}
                  currentObjectiveOrder={Number.MAX_SAFE_INTEGER}
                  className={layoutStyles.sidebarInDrawer}
                  accentPalette={accentPalette}
                />
              </DrawerBody>
            </Drawer>
          ) : (
            <InterviewSidebar
              diagnosticName={props.diagnostic?.name ?? ""}
              objectives={sidebarObjectives}
              currentObjectiveOrder={Number.MAX_SAFE_INTEGER}
              accentPalette={accentPalette}
            />
          )}
          <div className={layoutStyles.mainArea}>
            <div className={layoutStyles.sidebarToggleBar}>
              <button
                ref={toggleButtonRef}
                className={layoutStyles.sidebarToggleButton}
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open interview objectives"
                aria-expanded={isSidebarOpen}
              >
                <TextBulletListLtr20Regular aria-hidden="true" />
                Objectives
              </button>
            </div>
            <div
              className={`${styles.bottomSection} ${styles.finishedBottomSection}`}
              ref={historyListRef}
              tabIndex={0}
              role="region"
              aria-label="Completed interview transcript"
            >
              <AnswersHistory
                previousAnswers={[...previousAnswers, firstAnswer]}
                objectives={sidebarObjectives}
                accentPalette={accentPalette}
              />
            </div>

            <div className={styles.countdownFooter}>
              {countdownSeconds > 0 && (
                <div className={styles.countdownContainer}>
                  <Caption1 className={styles.countdownText}>
                    This interview will close in {countdownSeconds} seconds.
                  </Caption1>
                </div>
              )}
              <PoweredByFooter className={styles.compactFooter} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.page}>
      <div className={layoutStyles.interviewLayout}>
        {isMobile ? (
          <Drawer
            type="overlay"
            position="start"
            open={isSidebarOpen}
            onOpenChange={(_, { open }) => {
              setIsSidebarOpen(open);
              if (!open) {
                toggleButtonRef.current?.focus();
              }
            }}
            className={layoutStyles.sidebarDrawer}
            aria-label="Interview objectives"
          >
            <DrawerBody className={layoutStyles.sidebarDrawerBody}>
              <InterviewSidebar
                diagnosticName={props.diagnostic?.name ?? ""}
                objectives={sidebarObjectives}
                currentObjectiveOrder={currentObjective?.order ?? 0}
                className={layoutStyles.sidebarInDrawer}
                accentPalette={accentPalette}
              />
            </DrawerBody>
          </Drawer>
        ) : (
          <InterviewSidebar
            diagnosticName={props.diagnostic?.name ?? ""}
            objectives={sidebarObjectives}
            currentObjectiveOrder={currentObjective?.order ?? 0}
            accentPalette={accentPalette}
          />
        )}
        <div className={layoutStyles.mainArea}>
          <div className={layoutStyles.sidebarToggleBar}>
            <button
              ref={toggleButtonRef}
              className={layoutStyles.sidebarToggleButton}
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open interview objectives"
              aria-expanded={isSidebarOpen}
            >
              <TextBulletListLtr20Regular aria-hidden="true" />
              Objectives
            </button>
          </div>
          <div
            className={layoutStyles.historyArea}
            ref={historyListRef}
            tabIndex={0}
            role="region"
            aria-label="Interview conversation history"
          >
            <AnswersHistory
              previousAnswers={previousAnswers}
              currentQuestion={
                isMobile || !!pendingAnswer
                  ? [currentObjective?.introduction, currentObjective?.text]
                      .filter(Boolean)
                      .join("\n\n")
                  : undefined
              }
              objectives={sidebarObjectives}
              isLoading={loading}
              pendingAnswer={pendingAnswer}
              accentPalette={accentPalette}
            />
          </div>
          {!finished && currentObjective && !pendingAnswer && (
            <CurrentQuestionShelf
              question={currentObjective.text}
              objectiveName={
                props.diagnostic?.objectives?.find(
                  (o) => o.order === currentObjective.order,
                )?.name
              }
              accentPalette={accentPalette}
              transitionMessage={currentObjective.transitionMessage}
              introduction={currentObjective.introduction}
              statementNumber={currentObjective.statementNumber}
              totalStatements={currentObjective.totalStatements}
              objectiveType={currentObjective.objectiveType}
              language={selectedLanguage}
            />
          )}
          <div className={layoutStyles.inputArea}>
            <div className={styles.inputSectionWrapper}>
              <div className={styles.inputCard}>
                <div className={styles.tabBar} role="tablist">
                  <div className={styles.tabGroup}>
                    <button
                      role="tab"
                      aria-selected={inputTab === TABS.TEXT}
                      aria-label="Text input"
                      className={mergeClasses(
                        styles.tab,
                        inputTab === TABS.TEXT && styles.tabActive,
                      )}
                      style={
                        accentPalette && inputTab === TABS.TEXT
                          ? { borderColor: accentPalette.accent }
                          : undefined
                      }
                      onClick={() => setInputTab("text")}
                    >
                      <TextBulletListLtr20Regular aria-hidden="true" />
                      <span className={styles.tabLabel}>Text</span>
                    </button>
                    {distributionSpeechEnabled && (
                      <button
                        role="tab"
                        aria-selected={inputTab === TABS.VOICE}
                        aria-label="Voice input"
                        className={mergeClasses(
                          styles.tab,
                          inputTab === TABS.VOICE && styles.tabActive,
                        )}
                        style={
                          accentPalette && inputTab === TABS.VOICE
                            ? { borderColor: accentPalette.accent }
                            : undefined
                        }
                        onClick={() => {
                          setInputTab("voice");
                          setVoiceUiState(TAB_VOICE_STATE["voice"]);
                        }}
                      >
                        <Mic20Regular aria-hidden="true" />
                        <span className={styles.tabLabel}>Voice</span>
                      </button>
                    )}
                  </div>
                  <div className={styles.tabMeta}>
                      <Dropdown
                        className={styles.languageSelect}
                        value={`${selectedLanguage.flag} ${selectedLanguage.text}`}
                        selectedOptions={[selectedLanguage.key]}
                        disabled={switchingLanguages}
                        onOptionSelect={async (_, data) => {
                          const option = VOICE_OPTIONS.find(
                            (opt) =>
                              opt.text === data.optionText &&
                              opt.key !== "disabled",
                          );
                          if (option && option.text !== selectedLanguage.text) {
                            try {
                              setSwitchingLanguages(true);
                              await fetchNext(
                                ConstantValues.EMPTY_STRING,
                                option.text,
                              );
                            } finally {
                              setSwitchingLanguages(false);
                            }
                            setSelectedLanguage(option);
                          }
                        }}
                      >
                        {VOICE_OPTIONS.filter(
                          (opt) => opt.key !== "disabled",
                        ).map((option) => (
                          <Option key={option.key} text={option.text}>
                            {option.flag} {option.text}
                          </Option>
                        ))}
                      </Dropdown>
                      {inputTab === TABS.VOICE && distributionSpeechEnabled && (
                        <MicrophoneHelpDialog />
                      )}
                    </div>
                </div>
                {isLikertObjective &&
                  currentObjective &&
                  inputTab !== TABS.VOICE && (
                    <div role="tabpanel" className={styles.textPanel}>
                      <Text className={styles.likertPrompt}>
                        {selectedLanguage.likertPrompt}
                      </Text>
                      <LikertScalePicker
                        accentPalette={accentPalette}
                        key={`${currentObjective.questionNumber}-${currentObjective.textInEnglish}`}
                        labels={
                          currentObjective.selectOptions ??
                          DEFAULT_LIKERT_SCALE_LABELS
                        }
                        selectedValue={selectedLikertValue}
                        placeholder={selectedLanguage.multipleChoicePlaceholder}
                        disabled={loading}
                        onSelect={setSelectedLikertValue}
                      />
                      <div
                        className={mergeClasses(
                          styles.textPanelBody,
                          styles.likertActionsRow,
                        )}
                      >
                        {submitButton}
                        {skipButton}
                      </div>
                    </div>
                  )}
                {isMultipleChoiceObjective &&
                  currentObjective &&
                  inputTab !== TABS.VOICE && (
                    <div role="tabpanel" className={styles.textPanel}>
                      <Text className={styles.likertPrompt}>
                        {currentObjective.allowMultipleSelection === false
                          ? selectedLanguage.multipleChoicePromptSingle
                          : selectedLanguage.multipleChoicePrompt}
                      </Text>
                      <MultipleChoicePicker
                        accentPalette={accentPalette}
                        key={`${currentObjective.questionNumber}-${currentObjective.textInEnglish}`}
                        options={currentObjective.selectOptions ?? {}}
                        selectedValues={selectedMultipleChoiceValues}
                        placeholder={selectedLanguage.multipleChoicePlaceholder}
                        isMultiselect={
                          currentObjective.allowMultipleSelection !== false
                        }
                        disabled={loading}
                        onSelectionChange={setSelectedMultipleChoiceValues}
                      />
                      <div
                        className={mergeClasses(
                          styles.textPanelBody,
                          styles.likertActionsRow,
                        )}
                      >
                        {submitButton}
                        {skipButton}
                      </div>
                    </div>
                  )}
                {!isSelectionObjective && inputTab === TABS.TEXT && (
                  <div role="tabpanel" className={styles.textPanel}>
                    <div className={styles.textPanelBody}>
                      <Textarea
                        root={{ className: styles.textPanelTextareaRoot }}
                        appearance="outline"
                        readOnly={isAnswerInputBusy}
                        aria-disabled={isAnswerInputBusy || undefined}
                        value={answer}
                        minLength={MINIMUM_ANSWER_LENGTH}
                        maxLength={MAX_ANSWER_LENGTH}
                        placeholder={
                          currentObjective?.demographicQuestionId
                            ? selectedLanguage.demographicsPlaceholder
                            : selectedLanguage.responsePlaceholder
                        }
                        aria-describedby={error ? "answer-error" : undefined}
                        aria-invalid={error ? true : undefined}
                        textarea={{
                          className: mergeClasses(
                            styles.textPanelTextareaInner,
                            isAnswerInputBusy && styles.textPanelTextareaBusy,
                          ),
                          ref: textareaRef,
                          onInput,
                          onKeyDown: (e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (isAnswerInputBusy) return;
                              void doSubmit(answer);
                            }
                          },
                        }}
                        onChange={(e) => {
                          setAnswer(e.target.value);
                          if (error) setError(null);
                        }}
                      />
                      {submitButton}
                      {skipButton}
                    </div>
                    {error && (
                      <Text
                        id="answer-error"
                        role="alert"
                        className={styles.errorText}
                      >
                        {error}
                      </Text>
                    )}
                  </div>
                )}

                {inputTab === TABS.VOICE && distributionSpeechEnabled && (
                  <div role="tabpanel" className={styles.voicePanel}>
                    <div className={styles.hiddenVoiceControls}>
                      {!switchingLanguages && (
                        <VoiceControls
                          ref={voiceControlsRef}
                          diagnosticId={undefined}
                          distributionId={undefined}
                          enabled={voiceEnabled}
                          locale={voiceLocale}
                          voiceName={selectedLanguage.voiceName}
                          micDeviceId={micDeviceId}
                          onMicChange={setMicDeviceId}
                          questionText={currentObjective?.text}
                          answer={answer}
                          setAnswer={setAnswer}
                          isValid={(t: string) => !validateTextAreaValue(t)}
                          errorText={error ?? ConstantValues.EMPTY_STRING}
                          setErrorText={(msg: string) => setError(msg || null)}
                          onSubmit={doSubmit}
                          onListeningStart={handleListeningStart}
                          onAskingStart={handleAskingStart}
                          onVoiceStateUpdate={handleVoiceStateUpdate}
                        />
                      )}
                    </div>
                    {isSelectionObjective &&
                      inputTab === TABS.VOICE &&
                      voiceUiState !== "loading" && (
                        <div className={styles.voiceUnavailableWrapper}>
                          <div
                            className={styles.voiceUnavailableState}
                            role="status"
                            aria-live="polite"
                          >
                            <div
                              className={styles.voiceAvatar}
                              style={
                                accentPalette
                                  ? {
                                      backgroundColor: accentPalette.headerBg,
                                      color: accentPalette.headerText,
                                    }
                                  : undefined
                              }
                            >
                              <Mic28Regular />
                            </div>
                            <div className={styles.voiceDisabledStateInfo}>
                              <div className={styles.voiceStateStatus}>
                                <Text weight="semibold">
                                  Voice isn't available for this section
                                </Text>
                              </div>
                              <Caption1 className={styles.voiceStateSublabel}>
                                This section needs the answer options visible on
                                screen, so it's text only. Switch to Text to
                                answer the questions — you can return to Voice
                                for the rest of the interview.
                              </Caption1>
                            </div>
                          </div>
                          <div className={styles.voiceStateButtons}>
                            <Button
                              className={
                                accentPalette
                                  ? sharedButtonStyles.secondaryButton
                                  : sharedButtonStyles.primaryButton
                              }
                              style={getVoiceBtnStyle("switch-to-text")}
                              size="small"
                              onMouseEnter={() =>
                                setHoveredVoiceBtn("switch-to-text")
                              }
                              onMouseLeave={() => setHoveredVoiceBtn(null)}
                              onClick={() => {
                                setInputTab("text");
                              }}
                            >
                              Switch to text
                            </Button>
                          </div>
                        </div>
                      )}
                    {!isSelectionObjective && voiceUiState === "asking" && (
                      <div
                        className={styles.voiceState}
                        role="status"
                        aria-live="polite"
                      >
                        <div
                          className={styles.voiceAvatar}
                          style={
                            accentPalette
                              ? {
                                  backgroundColor: accentPalette.headerBg,
                                  color: accentPalette.headerText,
                                }
                              : undefined
                          }
                        >
                          <Mic28Regular />
                        </div>
                        <div className={styles.voiceStateInfo}>
                          <div className={styles.voiceStateStatus}>
                            <span
                              className={styles.voiceStateDot}
                              style={
                                accentPalette
                                  ? { backgroundColor: accentPalette.accent }
                                  : undefined
                              }
                            />
                            <Text weight="semibold">Asking Question...</Text>
                          </div>
                          <Caption1 className={styles.voiceStateSublabel}>
                            Please wait while the question is being read aloud
                          </Caption1>
                        </div>
                        <div className={styles.voiceStateButtons}>
                          <Button
                            className={sharedButtonStyles.outlineButton}
                            style={getVoiceBtnStyle("pause-asking")}
                            size="small"
                            onMouseEnter={() =>
                              setHoveredVoiceBtn("pause-asking")
                            }
                            onMouseLeave={() => setHoveredVoiceBtn(null)}
                            onClick={() => {
                              voiceControlsRef.current?.pause();
                              setVoiceUiState("paused");
                            }}
                          >
                            Pause Interview
                          </Button>
                          {skipButtonVoice}
                        </div>
                      </div>
                    )}

                    {!isSelectionObjective && voiceUiState === "listening" && (
                      <div
                        className={styles.voiceState}
                        role="status"
                        aria-live="polite"
                      >
                        <div
                          className={styles.voiceAvatar}
                          style={
                            accentPalette
                              ? {
                                  backgroundColor: accentPalette.headerBg,
                                  color: accentPalette.headerText,
                                }
                              : undefined
                          }
                        >
                          <Mic28Regular />
                        </div>
                        <div className={styles.voiceStateInfo}>
                          <div className={styles.voiceStateStatus}>
                            <span
                              className={styles.voiceStateDot}
                              style={
                                accentPalette
                                  ? { backgroundColor: accentPalette.accent }
                                  : undefined
                              }
                            />
                            <Text weight="semibold">Listening...</Text>
                          </div>
                          <Caption1 className={styles.voiceStateSublabel}>
                            {voiceIsDetecting && voiceSilenceTimer < 3
                              ? `Auto-sending in ${voiceSilenceTimer}s...`
                              : "Your message will auto-send after 3 seconds of silence"}
                          </Caption1>
                        </div>
                        <div className={styles.voiceStateButtons}>
                          <Button
                            className={sharedButtonStyles.outlineButton}
                            style={getVoiceBtnStyle("pause-listening")}
                            size="small"
                            onMouseEnter={() =>
                              setHoveredVoiceBtn("pause-listening")
                            }
                            onMouseLeave={() => setHoveredVoiceBtn(null)}
                            onClick={() => {
                              voiceControlsRef.current?.pause();
                              setVoiceUiState("paused");
                            }}
                          >
                            Pause Interview
                          </Button>
                          <Button
                            className={sharedButtonStyles.outlineButton}
                            style={getVoiceBtnStyle("play-listening")}
                            size="small"
                            onMouseEnter={() =>
                              setHoveredVoiceBtn("play-listening")
                            }
                            onMouseLeave={() => setHoveredVoiceBtn(null)}
                            onClick={() => {
                              voiceControlsRef.current?.playQuestionAgain();
                              setVoiceUiState("asking");
                            }}
                          >
                            Play Question Again
                          </Button>
                          {skipButtonVoice}
                        </div>
                      </div>
                    )}

                    {voiceUiState === "loading" && (
                      <div
                        className={styles.voiceState}
                        role="status"
                        aria-live="polite"
                      >
                        <div
                          className={styles.voiceAvatar}
                          style={
                            accentPalette
                              ? {
                                  backgroundColor: accentPalette.headerBg,
                                  color: accentPalette.headerText,
                                }
                              : undefined
                          }
                        >
                          <Spinner
                            size="small"
                            aria-label="Loading next question"
                            appearance={
                              accentPalette?.accentText === "#ffffff"
                                ? "inverted"
                                : undefined
                            }
                          />
                        </div>
                        <div className={styles.voiceStateInfo}>
                          <div className={styles.voiceStateStatus}>
                            <span className={styles.voiceStateDotPaused} />
                            <Text weight="semibold">
                              Loading Next Question...
                            </Text>
                          </div>
                          <Caption1 className={styles.voiceStateSublabel}>
                            Please wait a moment.
                          </Caption1>
                        </div>
                      </div>
                    )}

                    {voiceUiState === "paused" && (
                      <div
                        className={styles.voiceState}
                        role="status"
                        aria-live="polite"
                      >
                        <div
                          className={styles.voiceAvatar}
                          style={
                            accentPalette
                              ? {
                                  backgroundColor: accentPalette.headerBg,
                                  color: accentPalette.headerText,
                                }
                              : undefined
                          }
                        >
                          <Pause28Regular />
                        </div>
                        <div className={styles.voiceStateInfo}>
                          <div className={styles.voiceStateStatus}>
                            <span
                              className={styles.voiceStateDot}
                              style={
                                accentPalette
                                  ? { backgroundColor: accentPalette.accent }
                                  : undefined
                              }
                            />
                            <Text weight="semibold">Interview Paused</Text>
                          </div>
                          <Caption1 className={styles.voiceStateSublabel}>
                            Take your time. Click Resume when you're ready to
                            continue.
                          </Caption1>
                        </div>
                        <div className={styles.voiceStateButtons}>
                          <Button
                            className={sharedButtonStyles.outlineButton}
                            style={getVoiceBtnStyle("play-paused")}
                            size="small"
                            onMouseEnter={() =>
                              setHoveredVoiceBtn("play-paused")
                            }
                            onMouseLeave={() => setHoveredVoiceBtn(null)}
                            onClick={() => {
                              voiceControlsRef.current?.playQuestionAgain();
                              setVoiceUiState("asking");
                            }}
                          >
                            Play Question Again
                          </Button>
                          <Button
                            className={sharedButtonStyles.primaryButton}
                            style={voicePrimaryButtonStyle}
                            size="small"
                            onClick={() => {
                              voiceControlsRef.current?.resume();
                              setVoiceUiState("listening");
                            }}
                          >
                            Resume Interview
                          </Button>
                          {skipButtonVoice}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className={mergeClasses(
                  styles.kbdHint,
                  inputTab !== TABS.TEXT && styles.kbdHintInvisible,
                )}
                aria-hidden="true"
              >
                Enter to send · Shift+Enter for new line
              </div>
            </div>
          </div>
          <PoweredByFooter className={styles.compactFooter} />
        </div>
      </div>

      <Dialog
        open={showSkipConfirm}
        onOpenChange={(_, data) => setShowSkipConfirm(data.open)}
      >
        <DialogSurface aria-label={SKIP_CONFIRM_TITLE}>
          <DialogBody>
            <DialogTitle>{SKIP_CONFIRM_TITLE}</DialogTitle>
            <DialogContent>
              <Text>{SKIP_CONFIRM_MESSAGE}</Text>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setShowSkipConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                style={voicePrimaryButtonStyle}
                onClick={confirmSkip}
              >
                Skip objective
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
